import React, { useState, useRef } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Container,
    Alert,
    Autocomplete,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormHelperText,
    CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { internshipDomain } from '../util/domain';

const DEGREE_OPTIONS = ["B.E", "B.Tech", "M.E", "M.Tech", "MS", "Others"];
const SPECIALIZATION_OPTIONS = ["ECE", "EEE", "EE", "EIE", "CSE", "IT", "Others"];
const LOCATION_OPTIONS = ["hyderabad", "bangalore", "chennai", "online"];

const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return [currentYear.toString(), (currentYear - 1).toString(), (currentYear - 2).toString(), "Others"];
};

const QuickEnqueryForm = () => {
    // Step 1: Name, Phone, OTP
    //get query params
    const reference = new URLSearchParams(window.location.search).get('reference') || 'google';
    console.log("reference: ", reference);
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        otp: '',
        email: '',
        city: '',
        degree: '',
        otherDegree: '',
        specialization: '',
        otherSpecialization: '',
        year: '',
        otherYear: '',
        location: '',
        reference: reference,
        hashId: '',
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const otherDegreeRef = useRef(null);
    const otherSpecializationRef = useRef(null);
    const otherYearRef = useRef(null);

    // Handlers
    const handleChange = (e) => {
        if (e.target.name === 'phone') {
            // Only allow digits, max 10
            let value = e.target.value.replace(/\D/g, '').slice(0, 10);
            setFormData({ ...formData, phone: value });
        } else {
            setFormData({ ...formData, [e.target.name]: e.target.value });
        }
    };

    // Step 1: Send OTP
    const handleSendOtp = async () => {
        setError('');
        setSuccess('');
        setErrors({});
        // Validate name and phone
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
        else if (!/^\d{10}$/.test(formData.phone)) newErrors.phone = 'Enter valid 10-digit Indian phone number';
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;
        setLoading(true);
        try {
            // Replace with your OTP send API
            const response = await axios.post(`${internshipDomain}/api/verifyotp/send-otp`, { mobile: formData.phone });
            console.log("response: ", response);
            if (response.data.success && response.data.verified === false) {
                setFormData({ ...formData, hashId: response.data.hashId });
                setOtpSent(true);
                setSuccess('OTP sent to your phone.');
            } else if (response.data.success && response.data.verified === 1) {
                setOtpVerified(true);
                setStep(2);
                setSuccess('OTP verified. Please complete your details.');
            } else {
                setError(response.data.message);
            }
        } catch (err) {
            setError('Failed to send OTP.');
        } finally {
            setLoading(false);
        }
    };

    // Step 1: Verify OTP
    const handleVerifyOtp = async () => {
        setError('');
        setSuccess('');
        setErrors({});
        if (!formData.otp.trim()) {
            setErrors({ otp: 'OTP is required' });
            return;
        }
        setLoading(true);
        try {
            // Replace with your OTP verify API
            await axios.post(`${internshipDomain}/api/verifyotp/verify-otp`, { mobile: formData.phone, otp: formData.otp, hashId: formData.hashId });
            setOtpVerified(true);
            setStep(2);
            setSuccess('OTP verified. Please complete your details.');
        } catch (err) {
            setError('Invalid OTP.');
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Submit Details
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setErrors({});
        // Validate all fields
        const newErrors = {};
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';
        if (!formData.city.trim()) newErrors.city = 'City is required';
        if (!formData.degree) newErrors.degree = 'Degree is required';
        if (formData.degree === 'Others' && !formData.otherDegree.trim()) newErrors.otherDegree = 'Please specify degree';
        if (!formData.specialization) newErrors.specialization = 'Specialization is required';
        if (formData.specialization === 'Others' && !formData.otherSpecialization.trim()) newErrors.otherSpecialization = 'Please specify specialization';
        if (!formData.year) newErrors.year = 'Year of passout is required';
        if (formData.year === 'Others' && !formData.otherYear.trim()) newErrors.otherYear = 'Please specify year';
        if (!formData.location) newErrors.location = 'Preferred location is required';
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;
        setLoading(true);
        try {
            // Replace with your registration API
            await axios.post(`${internshipDomain}/api/quickenquery/save-enquery`, {
                name: formData.name,
                phone: formData.phone,
                studentemail: formData.email,
                city: formData.city,
                degree: formData.degree === 'Others' ? formData.otherDegree : formData.degree,
                specialization: formData.specialization === 'Others' ? formData.otherSpecialization : formData.specialization,
                reference: formData.reference,
                year: formData.year === 'Others' ? formData.otherYear : formData.year,
                location: formData.location,
            });
            setSuccess('Registration successful! We will get back to you soon.');
            setStep(3);
            //redirect to the home page after 5 seconds
            setTimeout(() => {
                window.location.href = 'https://vectorindia.org';
            }, 5000);
        } catch (err) {
            setError('Registration failed.');
        } finally {
            setLoading(false);
        }
    };
    function toProperCase(str) {
        return str.toLowerCase().split(' ').map(function (word) {
            return word.charAt(0).toUpperCase() + word.slice(1);
        }).join(' ');
    };

    return (
        <Container maxWidth="sm">
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 2 }}>
                <img src="/logo-vector.svg" alt="Vector India" style={{ height: '80px', width: 'auto' }} />
            </Box>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h5" align="center" gutterBottom>Quick Enquery</Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                {step === 1 && (
                    <Box>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="Full Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            error={!!errors.name}
                            helperText={errors.name}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="Phone Number"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            error={!!errors.phone}
                            helperText={errors.phone}
                            inputProps={{ maxLength: 10, pattern: "^\\d{10}$", inputMode: "tel" }}
                            InputProps={{
                                startAdornment: (
                                    <Typography sx={{ mr: 1, color: 'text.secondary', fontWeight: 500 }}>+91</Typography>
                                ),
                            }}
                        />
                        {!otpSent ? (
                            <Button variant="contained" sx={{ mt: 2 }} onClick={handleSendOtp} disabled={loading}>
                                {loading ? 'Saving...' : 'Submit'}
                            </Button>
                        ) : !otpVerified ? (
                            <Box>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="Enter OTP"
                                    name="otp"
                                    value={formData.otp}
                                    onChange={handleChange}
                                    error={!!errors.otp}
                                    helperText={errors.otp}
                                    inputProps={{ maxLength: 6, pattern: "[0-9]{6}", inputMode: "numeric" }}
                                />
                                <Button variant="contained" sx={{ mt: 2 }} onClick={handleVerifyOtp} disabled={loading}>
                                    {loading ? 'Verifying...' : 'Verify OTP'}
                                </Button>
                            </Box>
                        ) : null}
                    </Box>
                )}
                {step === 2 && (
                    <Box component="form" onSubmit={handleSubmit}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="Email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            type="email"
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="City"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            error={!!errors.city}
                            helperText={errors.city}
                        />
                        <FormControl fullWidth margin="normal" required error={!!errors.degree}>
                            <InputLabel>Degree</InputLabel>
                            <Select
                                name="degree"
                                value={formData.degree}
                                label="Degree"
                                onChange={(e) => {
                                    handleChange(e);
                                    if (e.target.value === 'Others') setTimeout(() => otherDegreeRef.current?.focus(), 0);
                                }}
                            >
                                {DEGREE_OPTIONS.map((deg) => (
                                    <MenuItem key={deg} value={deg}>{deg}</MenuItem>
                                ))}
                            </Select>
                            {errors.degree && <FormHelperText>{errors.degree}</FormHelperText>}
                        </FormControl>
                        {formData.degree === 'Others' && (
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                label="Please specify degree"
                                name="otherDegree"
                                value={formData.otherDegree}
                                onChange={handleChange}
                                inputRef={otherDegreeRef}
                                error={!!errors.otherDegree}
                                helperText={errors.otherDegree}
                            />
                        )}
                        <FormControl fullWidth margin="normal" required error={!!errors.specialization}>
                            <InputLabel>Specialization</InputLabel>
                            <Select
                                name="specialization"
                                value={formData.specialization}
                                label="Specialization"
                                onChange={(e) => {
                                    handleChange(e);
                                    if (e.target.value === 'Others') setTimeout(() => otherSpecializationRef.current?.focus(), 0);
                                }}
                            >
                                {SPECIALIZATION_OPTIONS.map((spec) => (
                                    <MenuItem key={spec} value={spec}>{spec}</MenuItem>
                                ))}
                            </Select>
                            {errors.specialization && <FormHelperText>{errors.specialization}</FormHelperText>}
                        </FormControl>
                        {formData.specialization === 'Others' && (
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                label="Please specify specialization"
                                name="otherSpecialization"
                                value={formData.otherSpecialization}
                                onChange={handleChange}
                                inputRef={otherSpecializationRef}
                                error={!!errors.otherSpecialization}
                                helperText={errors.otherSpecialization}
                            />
                        )}
                        <FormControl fullWidth margin="normal" required error={!!errors.year}>
                            <InputLabel>Year of Passout</InputLabel>
                            <Select
                                name="year"
                                value={formData.year}
                                label="Year of Passout"
                                onChange={(e) => {
                                    handleChange(e);
                                    if (e.target.value === 'Others') setTimeout(() => otherYearRef.current?.focus(), 0);
                                }}
                            >
                                {getYearOptions().map((year) => (
                                    <MenuItem key={year} value={year}>{year}</MenuItem>
                                ))}
                            </Select>
                            {errors.year && <FormHelperText>{errors.year}</FormHelperText>}
                        </FormControl>
                        {formData.year === 'Others' && (
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                label="Please specify year"
                                name="otherYear"
                                value={formData.otherYear}
                                onChange={handleChange}
                                inputRef={otherYearRef}
                                error={!!errors.otherYear}
                                helperText={errors.otherYear}
                            />
                        )}
                        <FormControl fullWidth margin="normal" required error={!!errors.location}>
                            <InputLabel>Preferred Location</InputLabel>
                            <Select
                                name="location"
                                value={formData.location}
                                label="Preferred Location"
                                onChange={handleChange}
                            >
                                {LOCATION_OPTIONS.map((loc) => (
                                    <MenuItem key={loc} value={loc}>{toProperCase(loc)}</MenuItem>
                                ))}
                            </Select>
                            {errors.location && <FormHelperText>{errors.location}</FormHelperText>}
                        </FormControl>
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                            <Button type="submit" variant="contained" disabled={loading}>
                                {loading ? 'Submitting...' : 'Submit'}
                            </Button>
                        </Box>
                    </Box>
                )}
                {step === 3 && (
                    <Typography variant="h6" align="center" color="success.main">Thank you for registering!</Typography>
                )}
            </Paper>
        </Container>
    );
};

export default QuickEnqueryForm; 